// /dealers: Phoneware's list of dealers, agent and wholesale. A dealer signed in
// sees its own profile here and nothing about anyone else. A Super admin acts as a
// dealer's person from the dealer's sheet (issue #1771, mock M1 states 51 and 52), and
// any Phoneware tier opens a dealer's person to see their accounts (issue #1776, mock M3).
import React, { useEffect, useRef, useState } from "react";
import { ACT_AS_IDLE_MINUTES, activityScopeOf, roleLabel, type AutotaskCategoryLink, type AutotaskOwner, type CustomerSummary, type Dealer, type DealerInput, type DealerModel, type LensUser, type NsDirectoryStatus, type NsResellerAssociationResult, type Quote, type SyncStatus } from "@phoneware/lens-domain";
import { categoryLinkOf, ownerSeedOf, type CategoryStatus } from "../api-customers.js";
import { ApiError } from "../api.js";
import { Icon } from "../icons.js";
import { Link, navigate, useLocation } from "../router.js";
import { useLoad, useSession, viewerKind } from "../session.js";
import { errorText, firstName, plural, sameMonth, stamp } from "../format.js";
import { Avatar, Callout, Chip, DealerMark, Head, Sheet, ViewOnly, useTitle, useToast } from "../ui.js";
import { NsAssociationSheet, NsDirectoryPanel, NsIdentity } from "./NsDirectory.js";
import { CreateDealerSheet, LinkCategorySheet } from "./CategorySheets.js";
import { CategoryName, personName, suggestDealer } from "./customer-bits.js";
import { PersonSheet } from "./PersonSheet.js";

const MODEL_WORD: Record<DealerModel, string> = { agent: "Dealer", wholesale: "Reseller" };

const NEW_DEALER_COLOR = "#24526B";

// A dealer with no account manager in Autotask cannot add customers, so the list says so where ops look.
function NoOwner({ testId }: { testId?: string }): React.ReactElement {
  return (
    <Chip kind="warn" icon="triangle-alert" testId={testId}>
      No account manager
    </Chip>
  );
}

// Ops pick a dealer's Autotask owner from the people Autotask lets own a company. A listbox rather
// than a <select> so each person's email can sit under their name.
function OwnerSelect({ value, current, onChange }: { value: AutotaskOwner | null; current: AutotaskOwner | null; onChange: (owner: AutotaskOwner | null) => void }): React.ReactElement {
  const { api } = useSession();
  const owners = useLoad<AutotaskOwner[]>(() => api.autotaskOwners(), []);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  // The owner a dealer has today stays listed even when Autotask no longer offers them, until ops pick another.
  const people = owners.data && current && !owners.data.some((o) => o.resourceId === current.resourceId) ? [current, ...owners.data] : owners.data ?? [];

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && e.target instanceof Node && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  useEffect(() => {
    if (!open || !list.current) return;
    const chosen = list.current.querySelector<HTMLButtonElement>('[aria-selected="true"]') ?? list.current.querySelector<HTMLButtonElement>('[role="option"]');
    chosen?.focus();
  }, [open, owners.data]);

  function choose(owner: AutotaskOwner | null): void {
    onChange(owner);
    setOpen(false);
    trigger.current?.focus();
  }

  function move(e: React.KeyboardEvent<HTMLUListElement>): void {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const options = Array.from(list.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);
      const at = options.findIndex((o) => o === document.activeElement);
      const next = Math.min(options.length - 1, Math.max(0, at + (e.key === "ArrowDown" ? 1 : -1)));
      options[next]?.focus();
    } else if (e.key === "Escape") {
      // Closes the list, not the sheet around it.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      trigger.current?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  }

  return (
    <div className="field">
      <span id="dealer-owner-label">Account manager in Autotask</span>
      <div className="combo" ref={box}>
        <button
          ref={trigger}
          className="input owner-pick"
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby="dealer-owner-label dealer-owner-value"
          data-testid="dealer-owner-select"
          onClick={() => setOpen(!open)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" && !open) {
              e.preventDefault();
              setOpen(true);
            }
          }}
        >
          <span id="dealer-owner-value" className="owner-pick-v">
            {value ? (
              <>
                <b>{value.name}</b>
                {value.email ? <small>{value.email}</small> : null}
              </>
            ) : (
              <span className="muted">None</span>
            )}
          </span>
          <Icon name="chevron-down" size={14} />
        </button>
        {open ? (
          <ul ref={list} className="combo-list owner-list" role="listbox" aria-labelledby="dealer-owner-label" onKeyDown={move}>
            <li>
              <button type="button" role="option" aria-selected={value === null} data-testid="dealer-owner-option-none" onClick={() => choose(null)}>
                <span className="owner-o">
                  <b>None</b>
                </span>
                {value === null ? <Icon name="check" size={14} /> : null}
              </button>
            </li>
            {owners.error ? (
              <li className="empty" role="alert">
                {owners.error.message}
              </li>
            ) : !owners.data ? (
              <li className="empty">Loading Autotask people.</li>
            ) : (
              people.map((o) => {
                const chosen = value?.resourceId === o.resourceId;
                return (
                  <li key={o.resourceId}>
                    <button type="button" role="option" aria-selected={chosen} data-testid={`dealer-owner-option-${o.resourceId}`} onClick={() => choose(o)}>
                      <span className="owner-o">
                        <b>{o.name}</b>
                        {o.email ? <span>{o.email}</span> : null}
                      </span>
                      {chosen ? <Icon name="check" size={14} /> : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        ) : null}
      </div>
      <p className="field-note">The Autotask resource that owns the companies Omni creates for this dealer. Phoneware sets it on the dealer.</p>
    </div>
  );
}

// Only what ops changed goes to PATCH, so choosing an owner sends the owner and nothing else.
function changes(dealer: Dealer, input: DealerInput): Partial<DealerInput> {
  const patch: Partial<DealerInput> = {};
  if (input.name !== dealer.name) patch.name = input.name;
  if (input.model !== dealer.model) patch.model = input.model;
  if (input.city !== dealer.city) patch.city = input.city;
  if (input.brand.color !== dealer.brand.color) patch.brand = input.brand;
  if (input.autotaskOwnerResourceId !== (dealer.autotaskOwner?.resourceId ?? null)) patch.autotaskOwnerResourceId = input.autotaskOwnerResourceId;
  return patch;
}

// Adds a dealer, or with `dealer` edits one. Ops only.
function DealerForm({ dealer, onSaved, onClose }: { dealer: Dealer | null; onSaved: (d: Dealer) => void; onClose: () => void }): React.ReactElement {
  const { api } = useSession();
  const [name, setName] = useState(dealer?.name ?? "");
  const [model, setModel] = useState<DealerModel>(dealer?.model ?? "agent");
  const [city, setCity] = useState(dealer?.city ?? "");
  const [color, setColor] = useState(dealer?.brand.color ?? NEW_DEALER_COLOR);
  const [owner, setOwner] = useState<AutotaskOwner | null>(dealer?.autotaskOwner ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const input: DealerInput = {
    name: name.trim(),
    model,
    city: city.trim() || null,
    brand: { color, logoDataUrl: dealer?.brand.logoDataUrl ?? null },
    autotaskOwnerResourceId: owner?.resourceId ?? null,
  };
  const patch = dealer ? changes(dealer, input) : null;
  const ready = input.name !== "" && (patch === null || Object.keys(patch).length > 0);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      onSaved(dealer && patch ? await api.updateDealer(dealer.id, patch) : await api.createDealer(input));
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Sheet
      title={dealer ? `Edit ${dealer.name}` : "Add a dealer"}
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" data-testid="dealer-save" disabled={!ready || busy} onClick={() => void save()}>
            <Icon name={dealer ? "check" : "plus"} size={15} />
            {dealer ? (busy ? "Saving" : "Save") : busy ? "Adding" : "Add the dealer"}
          </button>
        </>
      }
    >
      <div className="stack">
        <label className="field">
          <span>Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="field">
          <span id="dealer-model">Model</span>
          <div className="seg" role="group" aria-labelledby="dealer-model">
            {(["agent", "wholesale"] as DealerModel[]).map((m) => (
              <button key={m} type="button" aria-pressed={model === m} onClick={() => setModel(m)}>
                {MODEL_WORD[m]}
              </button>
            ))}
          </div>
        </div>
        <p className="card-note">
          {model === "agent" ? "A dealer sells at Phoneware's prices and is paid commission." : "A reseller buys at a flat cost and marks up; Phoneware still bills both."}
        </p>
        <label className="field">
          <span>City</span>
          <input className="input" value={city} onChange={(e) => setCity(e.target.value)} />
        </label>
        <OwnerSelect value={owner} current={dealer?.autotaskOwner ?? null} onChange={setOwner} />
        <div className="field">
          <span>Brand color</span>
          <input className="swatch-pick" type="color" aria-label="Brand color" value={color} onChange={(e) => setColor(e.target.value)} />
        </div>
        {dealer ? null : <p className="card-note">Creates an Omni-only dealer. Assign an NS key separately once it exists. Invite its people from Settings once it exists.</p>}
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            <span data-testid="dealer-form-error">{error}</span>
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}

const VIA_LINE: Record<string, string> = { exact_name: "Exact name match", nickname: "Nickname match", near_exact: "Near-exact name match" };
const VIA_CHIP: Record<string, string> = { exact_name: "Exact name", nickname: "Nickname", near_exact: "Near-exact name", ops: "Linked by ops", created: "Created from it" };

/** How the dealer came to hold its category, for the second line under it. */
function viaLine(link: AutotaskCategoryLink, users: LensUser[] | null): string {
  if (link.via === "ops") return `Linked by ${personName(users, link.linkedBy)} \u00b7 ${stamp(link.linkedAt)}`;
  if (link.via === "created") return `Dealer created from it by ${personName(users, link.linkedBy)}`;
  return VIA_LINE[link.via] ?? link.via;
}

function DealerSheet({
  dealer,
  people,
  users,
  category,
  customers,
  suggestion,
  manages,
  onEdit,
  onAssociate,
  onReviewLink,
  onActAs,
  onOpenPerson,
  onClose,
}: {
  dealer: Dealer;
  people: LensUser[];
  users: LensUser[] | null;
  /** The Autotask category as the last sync read it, when the dealer holds one. */
  category: CategoryStatus | null;
  customers: number;
  suggestion: CategoryStatus | null;
  /** Super admins add, edit and link dealers; the other tiers read them (dealers.manage). */
  manages: boolean;
  onEdit: () => void;
  onAssociate: () => void;
  onReviewLink: (category: CategoryStatus) => void;
  /** Opens the act-as start sheet for one of the dealer's people. */
  onActAs: (person: LensUser) => void;
  /** Opens the person sheet: their accounts, which every Phoneware tier reads (spec 3.6). */
  onOpenPerson: (person: LensUser) => void;
  onClose: () => void;
}): React.ReactElement {
  const { me, can } = useSession();
  // Act as is a Super admin's (act_as), on a dealer's active or invited person, never on yourself (spec 3.5).
  const canAct = can("act_as");
  const actsOn = (u: LensUser): boolean => {
    const kind = viewerKind(u);
    switch (kind) {
      case "admin":
        return false;
      case "dealer":
        return (u.status === "active" || u.status === "invited") && u.email !== me.user.email;
      default: {
        const unhandled: never = kind;
        throw new Error(`Unknown viewer ${String(unhandled)}`);
      }
    }
  };
  const link = categoryLinkOf(dealer);
  const seed = ownerSeedOf(dealer);
  return (
    <Sheet
      title={dealer.name}
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Close
          </button>
          {manages ? (
            <button className="btn primary" type="button" data-testid="dealer-edit" onClick={onEdit}>
              <Icon name="pen-line" size={15} />
              Edit
            </button>
          ) : null}
        </>
      }
    >
      <div className="stack">
        <div className="dealer-card">
          <DealerMark name={dealer.name} brand={dealer.brand} size={40} />
          <div>
            <b>{dealer.name}</b>
            <span className="row-m">{dealer.city ?? "No city on file"}</span>
          </div>
          <Chip>{MODEL_WORD[dealer.model]}</Chip>
        </div>
        {dealer.house ? (
          <>
            <h3>Phoneware{"\u2019"}s own sales</h3>
            <div className="callout" data-testid="dealer-sheet-house">
              <Icon name="store" size={15} />
              <div>
                <p>
                  Orders under {dealer.name} are Phoneware{"\u2019"}s own sales. A signed quote opens a <b>Post Sale</b> ticket with the New Contract Checklist, then a <b>Provisioning</b> ticket with the Phoneware Edge Provisioning checklist.
                </p>
              </div>
            </div>
            <p className="field-note">One dealer holds this. Ops set it in Omni{"\u2019"}s admin script, not here.</p>
          </>
        ) : null}
        <dl className="facts dealer-facts">
          <div>
            <dt>Pricing</dt>
            <dd>{dealer.model === "agent" ? "A dealer quotes list price, discounting down to the floor, and is paid commission." : "A reseller buys at a flat cost and marks up; Phoneware still bills both."}</dd>
          </div>
          <div>
            <dt>Customers</dt>
            <dd>{customers.toLocaleString("en-US")}</dd>
          </div>
          <div>
            <dt>NetSapiens reseller</dt>
            <dd><NsIdentity dealer={dealer} /></dd>
          </div>
          <div>
            <dt>Account manager in Autotask</dt>
            <dd>
              {dealer.autotaskOwner ? (
                <>
                  {dealer.autotaskOwner.name}
                  {dealer.autotaskOwner.email ? <span className="row-m">{dealer.autotaskOwner.email}</span> : null}
                </>
              ) : (
                <NoOwner />
              )}
            </dd>
          </div>
        </dl>
        {seed && dealer.autotaskOwner ? (
          <p className="field-note" data-testid="dealer-seed-note">
            Seeded from Autotask: owns {seed.companies.toLocaleString("en-US")} of {seed.ofCustomers.toLocaleString("en-US")} of this category's customers. Changing it clears the seed.
          </p>
        ) : null}
        {manages ? <button className="btn" type="button" data-testid="dealer-ns-associate" onClick={onAssociate}>NS association</button> : null}
        {dealer.autotaskOwner ? null : (
          <Callout kind="warn" icon="triangle-alert">
            {dealer.name} cannot add customers until Phoneware sets an account manager in Autotask on the dealer. Edit the dealer to choose one.
          </Callout>
        )}
        <h3>Autotask category</h3>
        {link ? (
          <p className="cat-line" data-testid="dealer-sheet-category">
            <b>
              <CategoryName name={link.name} nickname={link.nickname} />
            </b>
            <span className="row-m">
              Category {link.id} · {viaLine(link, users)}
              {category && !category.active ? " \u00b7 inactive in Autotask" : ""}
            </span>
          </p>
        ) : (
          <>
            <Callout kind="warn" icon="triangle-alert">
              {dealer.name} has no Autotask category. Omni files every new customer under its dealer's category, so {dealer.name} can't add customers until it has one.
            </Callout>
            {!manages ? null : suggestion ? (
              <button className="btn" type="button" data-testid="dealer-review-link" onClick={() => onReviewLink(suggestion)}>
                <Icon name="link" size={14} />
                Review link to {suggestion.name}
              </button>
            ) : (
              <p className="field-note">Link one from the Autotask categories on this page.</p>
            )}
          </>
        )}
        <h3>People</h3>
        {people.length ? (
          <ul className="rows people">
            {people.map((u) => (
              <li
                key={u.email}
                className="row"
                tabIndex={0}
                data-person={u.email}
                onClick={(e) => {
                  if (e.target instanceof Element && e.target.closest("button, a")) return;
                  onOpenPerson(u);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.target === e.currentTarget) onOpenPerson(u);
                }}
              >
                <Avatar name={u.name} />
                <div className="row-main">
                  <span className="row-t">{u.name}</span>
                  <span className="row-m">
                    {roleLabel(u.role)}, {u.scope === "all" ? "all customers" : "own customers"} · {u.status === "invited" ? "invited" : u.status === "active" ? "active" : "turned off"}
                  </span>
                </div>
                {canAct && actsOn(u) ? (
                  <div className="row-end">
                    <button className="btn small" type="button" data-testid={`act-as-${u.email}`} onClick={() => onActAs(u)}>
                      <Icon name="user" size={14} />
                      Act as
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{manages ? "Nobody yet. Invite them from Settings." : "Nobody yet."}</p>
        )}
        {canAct ? (
          <p className="card-note" data-testid="act-as-note">
            Act as shows Omni exactly as that person sees it, and records every change you make as you acting as them.
          </p>
        ) : (
          <p className="card-note" data-testid="no-act-as">
            Only a Super admin can act as someone.
          </p>
        )}
      </div>
    </Sheet>
  );
}

/** What Omni says when it won't start acting as someone (POST /api/act-as refusals, spec 3.5). */
function actAsRefusal(err: unknown, target: LensUser): string {
  if (!(err instanceof ApiError)) return errorText(err);
  const body = err.body;
  const reason = body && typeof body === "object" && "reason" in body ? body.reason : null;
  if (err.message === "cannot_act_as" && reason === "self") return "You can\u2019t act as yourself.";
  if (err.message === "cannot_act_as" && reason === "admin") return "Omni acts only as a dealer\u2019s people, not as someone at Phoneware.";
  if (err.message === "cannot_act_as" && reason === "disabled") return `${firstName(target.name)} is turned off, so Omni can\u2019t act as them.`;
  if (err.message === "forbidden") return "Only a Super admin can act as someone.";
  if (err.message === "not_found") return `Omni has nobody at ${target.email} any more.`;
  if (err.message === "not_while_acting_as") return "Stop acting as someone first.";
  return err.message;
}

/** The start sheet (mock M1 state 52): what acting as this person means, before it starts. */
function ActAsSheet({ target, dealer, owner, onClose }: { target: LensUser; dealer: Dealer; owner: LensUser | null; onClose: () => void }): React.ReactElement {
  const { me, startActingAs } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = firstName(target.name);
  const sees = owner
    ? `${owner.name}, ${dealer.name}\u2019s owner, and ${first} see this session and every change under Phoneware activity.`
    : `${first} sees this session and every change under Phoneware activity.`;
  const items: Array<{ icon: "eye" | "pen-line" | "users" | "lock" | "clock"; title: string; text: string }> = [
    {
      icon: "eye",
      title: `You see exactly what ${first} sees.`,
      text: `Omni shows you ${first}\u2019s screens and allows only what ${first} can do. Your ${roleLabel(me.user.role)} powers are off until you stop.`,
    },
    {
      icon: "pen-line",
      title: `Changes are ${first}\u2019s, with your name on the record.`,
      text: `What you change is saved as ${first}\u2019s, the same as when ${first} makes it. Omni\u2019s audit log records each change as ${me.user.name} acting as ${target.name}, and nobody can edit or delete that log.`,
    },
    { icon: "users", title: `${dealer.name} sees it.`, text: `${sees} They see \u201cPhoneware\u201d, not your name.` },
    // Logins (#1660) and Settings \u2192 Sign-in (#1774) join this line when they ship.
    { icon: "lock", title: "Some things are off.", text: `Connecting an AI or app is unavailable while you act as ${first}.` },
    {
      icon: "clock",
      title: "It ends when you stop.",
      text: `Click Stop on the banner or sign out. Omni also ends it after ${ACT_AS_IDLE_MINUTES} minutes with nothing done, if ${first} is turned off, or when you start acting as someone else.`,
    },
  ];
  async function start(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await startActingAs(target.email);
    } catch (err) {
      setError(actAsRefusal(err, target));
      setBusy(false);
    }
  }
  return (
    <Sheet
      title={`Act as ${target.name}`}
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" data-testid="start-act-as" disabled={busy} onClick={() => void start()}>
            <Icon name="user" size={15} />
            {busy ? "Starting" : `Act as ${first}`}
          </button>
        </>
      }
    >
      <span className="person">
        <Avatar name={target.name} />
        <span>
          <b>{target.name}</b>
          <span className="sub">
            {roleLabel(target.role)} at {dealer.name} · {target.scope === "all" ? "All customers" : "Own customers"} · {target.email}
          </span>
        </span>
      </span>
      {error ? (
        <Callout kind="stop" icon="triangle-alert">
          <span data-testid="act-as-refusal">{error}</span>
        </Callout>
      ) : null}
      <ul className="conseq">
        {items.map((item) => (
          <li key={item.icon}>
            <span className="row-ic">
              <Icon name={item.icon} />
            </span>
            <span>
              <b>{item.title}</b>
              {item.text}
            </span>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}

type CategorySheet = { kind: "link"; categoryId: number; dealerId: string | null } | { kind: "create"; categoryId: number | null };

function OpsDealers(): React.ReactElement {
  const { api, can, writeScope } = useSession();
  // Every Phoneware tier reads the dealers; Super admins add, edit and link them, and whoever runs syncs runs this one.
  const manages = writeScope("dealers.manage") === "any";
  const syncs = can("sync.run");
  const toast = useToast();
  const dealers = useLoad<Dealer[]>(() => api.dealers(), []);
  const users = useLoad<LensUser[]>(() => api.users(), []);
  const customers = useLoad<CustomerSummary[]>(() => api.customers(), []);
  const categories = useLoad<CategoryStatus[]>(() => api.autotaskCategories(), []);
  const sync = useLoad<SyncStatus>(() => api.syncStatus(), []);
  // Which dealers are selling: quotes each one started and got signed this month.
  const quotes = useLoad<Quote[]>(() => api.quotes(), []);
  const directory = useLoad<NsDirectoryStatus>(() => api.nsDirectory(), []);
  const [directoryBusy, setDirectoryBusy] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [associationId, setAssociationId] = useState<string | null>(null);
  const [form, setForm] = useState<{ dealer: Dealer | null } | null>(null);
  // /dealers?d=<id> opens that dealer's sheet: where Stop and the ended dialog bring a Super admin back to.
  const { query } = useLocation();
  const wanted = query.get("d");
  const [openId, setOpenId] = useState<string | null>(wanted);
  useEffect(() => {
    if (wanted) setOpenId(wanted);
  }, [wanted]);
  const closeDealer = () => {
    setOpenId(null);
    if (wanted) navigate("/dealers", { replace: true });
  };
  const [actAs, setActAs] = useState<{ target: LensUser; dealer: Dealer } | null>(null);
  const [person, setPerson] = useState<{ user: LensUser; dealer: Dealer } | null>(null);
  const [sheet, setSheet] = useState<CategorySheet | null>(null);
  const [syncing, setSyncing] = useState(false);
  const list = [...(dealers.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const cats = categories.data ?? [];
  const all = customers.data ?? [];
  const linked = list.filter((d) => categoryLinkOf(d) !== null).length;
  const open = list.find((d) => d.id === openId);
  const association = list.find((d) => d.id === associationId);
  const waitingBy: Record<string, number> = {};
  for (const w of sync.data?.customers?.waiting ?? []) waitingBy[String(w.categoryId)] = (waitingBy[String(w.categoryId)] ?? 0) + 1;
  const suggestionFor = (d: Dealer) => (categoryLinkOf(d) ? null : cats.find((c) => suggestDealer(c, list)?.dealer.id === d.id) ?? null);
  const theirs = (d: Dealer) => all.filter((c) => c.dealerId === d.id);
  const linkCategory = sheet?.kind === "link" ? cats.find((c) => c.id === sheet.categoryId) ?? null : null;

  async function reloadDirectory(): Promise<void> {
    const [nextDealers, nextDirectory] = await Promise.all([api.dealers(), api.nsDirectory()]);
    dealers.set(nextDealers);
    directory.set(nextDirectory);
  }

  async function reloadLinks(): Promise<{ dealers: Dealer[]; categories: CategoryStatus[] }> {
    const [nextDealers, nextCategories] = await Promise.all([api.dealers(), api.autotaskCategories()]);
    dealers.set(nextDealers);
    categories.set(nextCategories);
    return { dealers: nextDealers, categories: nextCategories };
  }

  async function syncNow(): Promise<void> {
    setSyncing(true);
    try {
      const run = await api.runSync({ steps: ["customers"] });
      const step = run.steps.customers;
      const [nextCustomers, nextSync] = await Promise.all([api.customers(), api.syncStatus(), reloadLinks()]);
      customers.set(nextCustomers);
      sync.set(nextSync);
      toast(step?.outcome === "failed" ? `The sync failed: ${step.error ?? "Autotask did not answer."}` : `Synced. ${plural(step?.wrote.owned ?? 0, "customer")} moved under a dealer.`);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setSyncing(false);
    }
  }

  async function refreshDirectory(syncDirectory: boolean): Promise<void> {
    setDirectoryBusy(true);
    setDirectoryError(null);
    let failure: string | null = null;
    try {
      if (syncDirectory) {
        const result = await api.syncNsDealers();
        directory.set(result.status);
        toast(`${plural(result.created, "dealer")} created, ${result.verified} existing verified.`);
      }
    } catch (err) {
      failure = errorText(err);
    }
    // A failed sync may have committed individual keys or a newer attempt. Always read both back.
    try {
      await reloadDirectory();
    } catch (err) {
      failure = failure ? `${failure} Refresh failed: ${errorText(err)}` : errorText(err);
    }
    setDirectoryError(failure);
    setDirectoryBusy(false);
  }

  async function associationChanged(result: NsResellerAssociationResult): Promise<void> {
    dealers.set(list.map((d) => {
      const changed = result.dealers.find((x) => x.id === d.id);
      return changed ? { ...changed, house: d.house } : d;
    }));
    await reloadDirectory();
    setDirectoryError(null);
    toast("NS association saved. Each dealer keeps its people and history.");
  }

  function categoryCell(d: Dealer): React.ReactElement {
    const link = categoryLinkOf(d);
    if (!link) {
      const suggestion = suggestionFor(d);
      const kept = theirs(d).filter((c) => c.categoryCheck.exception);
      return (
        <>
          <Chip kind="warn" icon="triangle-alert" testId={`dealer-category-${d.id}`} status="none">
            No category
          </Chip>
          {suggestion ? (
            <span className="sub">
              Suggested: {suggestion.name}
              {manages ? " \u00b7 " : ""}
              <button className="link" type="button" hidden={!manages} data-testid={`dealer-suggestion-${d.id}`} onClick={(e) => { e.stopPropagation(); setSheet({ kind: "link", categoryId: suggestion.id, dealerId: d.id }); }}>
                Review
              </button>
            </span>
          ) : kept.length ? (
            <span className="sub">{kept.length === 1 ? `Holds ${kept[0].name} as a Phoneware exception` : `Holds ${kept.length} customers as Phoneware exceptions`}</span>
          ) : (
            <span className="sub">Can't add customers</span>
          )}
        </>
      );
    }
    const category = cats.find((c) => c.id === link.id);
    if (category && !category.active) {
      return (
        <>
          <Chip kind="stop" icon="ban" testId={`dealer-category-${d.id}`} status="gone">
            Category gone
          </Chip>
          <span className="sub">{link.name} is inactive in Autotask</span>
        </>
      );
    }
    return (
      <>
        <b data-testid={`dealer-category-${d.id}`}>
          <CategoryName name={link.name} nickname={link.nickname} />
        </b>
        <span className="sub">{viaLine(link, users.data)}</span>
      </>
    );
  }

  function ownerCell(d: Dealer): React.ReactElement {
    if (!d.autotaskOwner) {
      return (
        <>
          <Chip kind="warn" icon="triangle-alert" testId={`dealer-owner-${d.id}`}>
            None
          </Chip>
          <span className="sub">Can't add customers</span>
        </>
      );
    }
    const seed = ownerSeedOf(d);
    return (
      <>
        <b data-testid={`dealer-owner-${d.id}`}>{d.autotaskOwner.name}</b>
        <span className="sub">{seed ? `Seeded: owns ${seed.companies.toLocaleString("en-US")} of ${seed.ofCustomers.toLocaleString("en-US")}` : "Set in Omni"}</span>
      </>
    );
  }

  function countCell(d: Dealer): React.ReactElement {
    const mine = theirs(d);
    const link = categoryLinkOf(d);
    const coming = link ? waitingBy[String(link.id)] ?? 0 : 0;
    const prospects = mine.filter((c) => c.companyType === 3).length;
    const leads = mine.filter((c) => c.companyType === 2).length;
    const extra = [prospects ? plural(prospects, "prospect") : "", leads ? plural(leads, "lead") : ""].filter(Boolean).join(", ");
    return (
      <>
        <b data-testid={`dealer-customers-${d.id}`}>{mine.filter((c) => c.companyType === 1).length.toLocaleString("en-US")}</b>
        {coming ? <span className="sub">{coming.toLocaleString("en-US")} on the next sync</span> : extra ? <span className="sub">Plus {extra}</span> : null}
      </>
    );
  }

  function quotesCell(d: Dealer): React.ReactElement {
    const mine = (quotes.data ?? []).filter((q) => q.dealerId === d.id);
    const quoted = mine.filter((q) => sameMonth(q.createdAt)).length;
    const signed = mine.filter((q) => q.signature !== null && sameMonth(q.signature.signedAt)).length;
    return (
      <>
        <b data-testid={`dealer-quoted-${d.id}`}>{quoted} quoted</b>
        <span className="sub" data-testid={`dealer-signed-${d.id}`}>
          {signed} signed
        </span>
      </>
    );
  }

  function pairRow(c: CategoryStatus): React.ReactElement {
    const at = (
      <div className="at">
        <b>
          <CategoryName name={c.name} nickname={c.nickname} />
        </b>
        <span className="meta">
          Category {c.id}
          {c.isDefault ? " \u00b7 Autotask default" : ""} · {plural(c.customerCount, "active customer")}
          {c.active ? "" : " \u00b7 inactive"}
        </span>
      </div>
    );
    const coming = waitingBy[String(c.id)] ?? 0;
    const holder = c.dealerId ? list.find((d) => d.id === c.dealerId) ?? null : null;
    const link = holder ? categoryLinkOf(holder) : null;
    if (holder && link) {
      const auto = link.via in VIA_LINE;
      const count = theirs(holder).length;
      const kept = all.filter((x) => x.categoryCheck.exception && x.categoryCheck.autotaskCategoryId === c.id && x.dealerId !== holder.id);
      const keptBy = [...new Set(kept.map((x) => list.find((d) => d.id === x.dealerId)?.name ?? x.dealerId))];
      const meta = !c.active
        ? `Keeps its ${plural(count, "customer")}. Can't add new ones until it has an active category.`
        : coming
          ? `${plural(coming, "customer")} move here on the next sync`
          : link.via === "ops"
            ? `Linked by ${personName(users.data, link.linkedBy)} \u00b7 ${stamp(link.linkedAt)}`
            : link.via === "created"
              ? `Created by ${personName(users.data, link.linkedBy)} \u00b7 ${stamp(link.linkedAt)}`
              : `${plural(count, "customer")}${kept.length ? ` \u00b7 ${kept.length} kept under ${keptBy.join(", ")}` : ""}`;
      return (
        <div className={c.active ? "pair" : "pair inactive"} key={c.id} data-category={c.id}>
          {at}
          <div className={c.active ? "via" : "via gone"}>
            {c.active ? (
              <Chip kind={auto ? "ok" : ""} icon={link.via === "created" ? "plus" : "link"}>
                {VIA_CHIP[link.via] ?? link.via}
              </Chip>
            ) : (
              <Chip kind="stop" icon="ban">
                Category gone
              </Chip>
            )}
          </div>
          <div className="om">
            <b>{holder.name}</b>
            <span className="meta">{meta}</span>
          </div>
        </div>
      );
    }
    const suggestion = suggestDealer(c, list);
    return (
      <div className="pair" key={c.id} data-category={c.id}>
        {at}
        <div className="via open">
          <Chip kind="warn">Needs a dealer</Chip>
        </div>
        <div className="om slot">
          {suggestion ? (
            <>
              <div>
                <b>Suggested: {suggestion.dealer.name}</b>
                <span className="meta">{suggestion.why}</span>
              </div>
              <div className="acts" hidden={!manages}>
                <button className="btn small primary" type="button" data-testid={`category-review-${c.id}`} onClick={() => setSheet({ kind: "link", categoryId: c.id, dealerId: suggestion.dealer.id })}>
                  Review link
                </button>
                <button className="btn small" type="button" onClick={() => setSheet({ kind: "link", categoryId: c.id, dealerId: null })}>
                  Another dealer
                </button>
              </div>
            </>
          ) : (
            <>
              <div>
                <b>No dealer matches</b>
                <span className="meta">{plural(coming || c.customerCount, "customer")} wait for one</span>
              </div>
              <div className="acts" hidden={!manages}>
                <button className="btn small primary" type="button" data-testid={`category-create-${c.id}`} onClick={() => setSheet({ kind: "create", categoryId: c.id })}>
                  Create dealer
                </button>
                <button className="btn small" type="button" data-testid={`category-link-${c.id}`} onClick={() => setSheet({ kind: "link", categoryId: c.id, dealerId: null })}>
                  Link a dealer
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const shown = cats.filter((c) => c.active || c.dealerId);
  const autoLinked = list.filter((d) => {
    const link = categoryLinkOf(d);
    return link !== null && link.via in VIA_LINE;
  }).length;
  const need = cats.filter((c) => c.active && !c.dealerId).length;
  const gone = cats.filter((c) => !c.active && c.dealerId).length;
  const waitingTotal = sync.data?.customers?.counts.waiting ?? 0;
  const lastSync = sync.data?.customers?.lastOkAt ?? null;

  return (
    <>
      <Head
        title="Dealers"
        sub={dealers.data ? `${plural(list.length, "dealer")} \u00b7 ${linked} linked to an Autotask category` : "Loading dealers."}
        actions={
          manages ? (
            <button className="btn primary" type="button" onClick={() => setForm({ dealer: null })}>
              <Icon name="plus" size={15} />
              Add a dealer
            </button>
          ) : null
        }
      />
      {manages ? null : (
        <div className="gap-b">
          <ViewOnly testId="dealers-view-only">Super admins add, edit and link dealers. You can see every dealer, its people and its links.</ViewOnly>
        </div>
      )}
      <section className="card" data-testid="dealers-page" aria-label="Dealers">
        {dealers.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {dealers.error.message}
          </Callout>
        ) : null}
        {!dealers.data ? (
          <div className="empty">Loading dealers.</div>
        ) : list.length === 0 ? (
          <div className="empty">No dealers yet. Add the first one, then invite its people from Settings.</div>
        ) : (
          <div className="tablewrap">
            <table className="t t-dealers dealers-links">
              <thead>
                <tr>
                  <th>Dealer</th>
                  <th>Pricing</th>
                  <th>Category</th>
                  <th>Account manager in Autotask</th>
                  <th className="num">Customers</th>
                  <th className="num">Quotes</th>
                </tr>
              </thead>
              <tbody>
                {list.map((d) => (
                  <tr key={d.id} data-dealer={d.id} onClick={() => setOpenId(d.id)}>
                    <td>
                      <span className="person">
                        <DealerMark name={d.name} brand={d.brand} size={26} />
                        <span>
                          <button className="ns-dealer-open" type="button" onClick={(e) => { e.stopPropagation(); setOpenId(d.id); }}>{d.name}</button>
                          {/* The house dealer (Phoneware's own sales, plan 3.2): GET /api/dealers marks it from lens_settings/house_dealer. */}
                          {d.house ? (
                            <span className="house-b">
                              <Chip kind="house" icon="store" testId="dealer-house">
                                Phoneware{"\u2019"}s own sales
                              </Chip>
                            </span>
                          ) : null}
                          <span className="sub">{d.city ?? ""}</span>
                        </span>
                      </span>
                      <NsIdentity dealer={d} />
                    </td>
                    <td data-label="Pricing">{MODEL_WORD[d.model]}</td>
                    <td data-label="Category">{categoryCell(d)}</td>
                    <td data-label="Account manager in Autotask">{ownerCell(d)}</td>
                    <td className="num" data-label="Customers">{countCell(d)}</td>
                    <td className="num" data-label="Quotes this month">{quotesCell(d)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="card-note">Quotes are this month: quoted, then signed. Open a dealer to see its people, its category and its account manager, or to edit it.</p>
        {list.some((d) => d.house) ? (
          <p className="card-note" data-testid="dealers-house-note">
            Phoneware{"\u2019"}s own sales is the dealer Phoneware{"\u2019"}s own reps quote under. One dealer holds it; Phoneware sets it in Omni{"\u2019"}s admin script, not on this page.
          </p>
        ) : null}
      </section>
      <section className="card" id="categories" data-testid="categories-panel" aria-label="Autotask categories">
        <div className="card-h">
          <h2>Autotask categories</h2>
          <span className="note">One dealer per category{lastSync ? ` \u00b7 last sync ${stamp(lastSync)}` : " \u00b7 not synced yet"}</span>
        </div>
        {categories.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {categories.error.message}
          </Callout>
        ) : !categories.data ? (
          <div className="empty">Loading Autotask categories.</div>
        ) : (
          <>
            <div className="cats-sum" data-testid="categories-summary">
              <Chip kind="quiet">{cats.filter((c) => c.active).length} active in Autotask</Chip>
              <Chip kind="ok" icon="link">
                {autoLinked} linked automatically
              </Chip>
              {need ? (
                <Chip kind="warn" icon="triangle-alert">
                  {need} need a dealer
                </Chip>
              ) : (
                <Chip kind="ok" icon="check">
                  Every category has a dealer
                </Chip>
              )}
              {gone ? (
                <Chip kind="stop" icon="ban">
                  {gone} gone
                </Chip>
              ) : null}
              {waitingTotal ? (
                <Chip kind="warn" icon="clock">
                  {waitingTotal.toLocaleString("en-US")} customers waiting
                </Chip>
              ) : null}
            </div>
            <div className="pair-h">
              <span>In Autotask</span>
              <span className="c">Link</span>
              <span>In Omni</span>
            </div>
            {shown.map(pairRow)}
          </>
        )}
        <p className="ns-note">Omni can't create Autotask categories. A person adds a new dealer's category in Autotask admin, and it shows up here on the next sync.</p>
        <div className="btnrow" hidden={!syncs}>
          <button className="btn" type="button" data-testid="categories-sync" disabled={syncing} onClick={() => void syncNow()}>
            <Icon name="refresh-cw" size={14} />
            {syncing ? "Syncing" : "Sync now"}
          </button>
        </div>
      </section>
      <NsDirectoryPanel
        status={directory.data}
        error={directoryError ?? directory.error?.message ?? null}
        busy={directoryBusy || directory.loading}
        onSync={manages ? () => void refreshDirectory(true) : null}
        onReload={() => void refreshDirectory(false)}
        dealers={list}
        onReview={manages ? (id) => setAssociationId(id) : null}
      />
      {form ? (
        <DealerForm
          dealer={form.dealer}
          onClose={() => setForm(null)}
          onSaved={(d) => {
            setForm(null);
            if (form.dealer) {
              // A saved dealer comes back without the house mark, which is the setting's; keep it.
              dealers.set((dealers.data ?? []).map((x) => (x.id === d.id ? { ...d, house: x.house } : x)));
              setOpenId(d.id);
              toast(`Saved ${d.name}.`);
            } else {
              dealers.set([...(dealers.data ?? []), d]);
              toast(`Added ${d.name}. Invite its people from Settings.`);
            }
          }}
        />
      ) : null}
      {open ? (
        <DealerSheet
          dealer={open}
          people={(users.data ?? []).filter((u) => u.dealerId === open.id)}
          users={users.data}
          category={cats.find((c) => c.id === categoryLinkOf(open)?.id) ?? null}
          customers={theirs(open).length}
          suggestion={suggestionFor(open)}
          manages={manages}
          onEdit={() => {
            setForm({ dealer: open });
            setOpenId(null);
          }}
          onAssociate={() => { setAssociationId(open.id); setOpenId(null); }}
          onReviewLink={(c) => { setOpenId(null); setSheet({ kind: "link", categoryId: c.id, dealerId: open.id }); }}
          onActAs={(person) => {
            setActAs({ target: person, dealer: open });
            setOpenId(null);
          }}
          onOpenPerson={(u) => {
            setPerson({ user: u, dealer: open });
            setOpenId(null);
          }}
          onClose={closeDealer}
        />
      ) : null}
      {actAs ? (
        <ActAsSheet
          key={actAs.target.email}
          target={actAs.target}
          dealer={actAs.dealer}
          owner={
            // Who else reads this session: the dealer's people who see every session there (its owner).
            (users.data ?? []).find(
              (u) => u.dealerId === actAs.dealer.id && activityScopeOf(u.role) === "dealer_sessions" && u.status === "active" && u.email !== actAs.target.email,
            ) ?? null
          }
          onClose={() => {
            setOpenId(actAs.dealer.id);
            setActAs(null);
          }}
        />
      ) : null}
      {person ? (
        <PersonSheet
          person={person.user}
          dealerName={person.dealer.name}
          onClose={() => {
            setOpenId(person.dealer.id);
            setPerson(null);
          }}
        />
      ) : null}
      {association ? <NsAssociationSheet
        key={association.id}
        dealer={association}
        dealers={list}
        status={directory.data}
        onReload={reloadDirectory}
        onChanged={associationChanged}
        onClose={() => setAssociationId(null)}
      /> : null}
      {sheet?.kind === "link" && linkCategory ? (
        <LinkCategorySheet
          key={`${linkCategory.id}-${sheet.dealerId ?? ""}`}
          category={linkCategory}
          dealers={list}
          users={users.data}
          waiting={sync.data?.customers ? waitingBy[String(linkCategory.id)] ?? 0 : null}
          dealerId={sheet.dealerId}
          reload={reloadLinks}
          onClose={() => setSheet(null)}
        />
      ) : null}
      {sheet?.kind === "create" ? (
        <CreateDealerSheet
          categories={cats}
          waiting={waitingBy}
          categoryId={sheet.categoryId}
          reload={reloadLinks}
          onClose={() => setSheet(null)}
        />
      ) : null}
    </>
  );
}

function OwnDealer({ dealer }: { dealer: Dealer }): React.ReactElement {
  const { api } = useSession();
  const customers = useLoad<CustomerSummary[]>(() => api.customers(), []);
  return (
    <>
      <Head
        title="Your dealer"
        sub="Phoneware sees every dealer here. You see your own."
        actions={
          <Link className="btn primary" to="/settings">
            <Icon name="settings" size={15} />
            Brand, people and pricing
          </Link>
        }
      />
      <section className="card">
        <div className="dealer-card">
          <DealerMark name={dealer.name} brand={dealer.brand} size={44} />
          <div>
            <b>{dealer.name}</b>
            <span className="row-m">{dealer.city ?? ""}</span>
          </div>
          <Chip>{dealer.model === "agent" ? "Dealer" : "Reseller"}</Chip>
        </div>
        <dl className="facts dealer-facts">
          <div>
            <dt>Customers</dt>
            <dd>{customers.data ? customers.data.length : ""}</dd>
          </div>
          <div>
            <dt>Model</dt>
            <dd>{dealer.model === "agent" ? "A dealer sells at Phoneware's prices and is paid commission." : "A reseller buys at a flat cost and marks up; Phoneware still bills both."}</dd>
          </div>
        </dl>
      </section>
    </>
  );
}

export function Dealers(): React.ReactElement {
  const { me } = useSession();
  useTitle("Dealers");
  const kind = viewerKind(me.user);
  switch (kind) {
    case "admin":
      return <OpsDealers />;
    case "dealer":
      return me.dealer ? <OwnDealer dealer={me.dealer} /> : <Head title="Your dealer" sub="No dealer is assigned to this user." />;
    default: {
      const unhandled: never = kind;
      throw new Error(`Unknown viewer ${String(unhandled)}`);
    }
  }
}
